/**
 * useWorkDescription — the "Meilleure description de votre travail ?" field
 * (Général → Profil, claude.ai parity).
 *
 * A dropdown of fixed, brand-neutral job families persisted per user via the
 * shared `/agent/preferences` contract (same query key as useCustomInstructions,
 * so both read one cache entry). The dropdown saves on change: `save(next)`
 * PUTs only the `workDescription` slice — the drafting model and custom
 * instructions are never clobbered — and surfaces per-field loading / saved /
 * error state honestly.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentApi, type AgentPreferences } from '@/api/agent';

/** Fallback option ids when the server response omits the catalog (older API / mock). */
export const WORK_DESCRIPTION_OPTIONS = [
  'marketing',
  'medical',
  'market_access',
  'regulatory',
  'research',
  'data_analytics',
  'sales',
  'operations',
  'strategy',
  'communications',
  'product',
  'consulting',
  'education',
  'other',
] as const;

/** Shared with OsShell's model query + useCustomInstructions so all read one cache entry. */
const PREFERENCES_KEY = ['agent', 'preferences'] as const;
const SAVED_FLASH_MS = 2000;

export interface UseWorkDescription {
  /** Current stored value ('' when unanswered). */
  value: string;
  /** Option ids to render (from the server, or the local fallback). */
  options: string[];
  /** Persist a new choice ('' clears it). */
  save: (next: string) => void;
  /** A save is in flight. */
  saving: boolean;
  /** True for a short window right after a successful save. */
  saved: boolean;
  /** The last save failed to reach the server. */
  error: boolean;
  /** The initial server load is in flight. */
  loading: boolean;
}

export function useWorkDescription(enabled = true): UseWorkDescription {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async () => (await agentApi.getPreferences()).data,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(false);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  const flash = useCallback(() => {
    setSaved(true);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setSaved(false), SAVED_FLASH_MS);
  }, []);

  const mutation = useMutation({
    mutationFn: (next: string) =>
      agentApi.updatePreferences({ workDescription: next === '' ? null : next }).then((r) => r.data),
    onSuccess: (data: AgentPreferences) => {
      queryClient.setQueryData(PREFERENCES_KEY, data);
      setError(false);
      flash();
    },
    onError: () => {
      setError(true);
      setSaved(false);
    },
  });

  const save = useCallback(
    (next: string) => {
      setSaved(false);
      setError(false);
      mutation.mutate(next);
    },
    [mutation],
  );

  const serverOptions = query.data?.workDescriptionOptions;
  const options =
    Array.isArray(serverOptions) && serverOptions.length
      ? serverOptions
      : [...WORK_DESCRIPTION_OPTIONS];

  return {
    value: query.data?.workDescription ?? '',
    options,
    save,
    saving: mutation.isPending,
    saved,
    error,
    loading: enabled && query.isLoading,
  };
}
